import React, { type ReactElement, useCallback, useEffect, useState, type ReactNode } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { AnalyticsAttributes } from '../../../../common/types/analytics/index.tsx';
import type { Command } from '../../../../common/types/commands/index.tsx';
import { useCommandPaletteExpandedCommand } from '../../../../controllers/command-palette/index.tsx';

export const CommandBodyErrorBoundary = ({
	teamName,
	attributes,
	fallback,
	children,
}: {
	teamName?: string;
	attributes?: AnalyticsAttributes;
	fallback?: (props: { error: Error }) => ReactElement;
	children: ReactNode;
	expandedCommand?: Command;
}) => {
	const [{ expandedCommand }] = useCommandPaletteExpandedCommand();

	const [capturedError, setCapturedError] = useState<Error | undefined>();
	const [prevExpandedCommand, setPrevExpandedCommand] = useState(expandedCommand);
	const [hasReset, setHasReset] = useState(false);
	useEffect(() => {
		if (hasReset) {
			setCapturedError(undefined);
			setHasReset(false);
		}
	}, [hasReset]);

	const onRetry = useCallback(() => {
		setHasReset(true);
	}, []);

	// This is so that changing body triggers a reset if an error was caught and then the user goes back a level or to another command list
	// Without this, the body would not rerender, and the fallback would remain present
	useEffect(() => {
		if (capturedError && !hasReset && prevExpandedCommand !== expandedCommand) {
			onRetry();
		}
		setPrevExpandedCommand(expandedCommand);
	}, [capturedError, expandedCommand, hasReset, onRetry, prevExpandedCommand]);

	return (
		<JSErrorBoundary
			id="command.body"
			packageName="jiraCommandPalette"
			teamName={teamName || 'deliveroo'}
			attributes={attributes}
			onError={(_location, error) => {
				setCapturedError(error);
			}}
			fallback={fallback || 'unmount'}
			resetCaughtError={hasReset ? capturedError : undefined}
		>
			{children}
		</JSErrorBoundary>
	);
};
